import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Grid,
    Toolbar,
    Typography,
    CircularProgress,
    Button,
    Pagination,
    TextField,
    Card,
    CardContent,
    Divider,
    Stack,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchRounded from "@mui/icons-material/SearchRounded";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import {
    CurrencyRupeeSharp,
    KeyboardArrowLeftOutlined,
    ReceiptLongOutlined,
} from "@mui/icons-material";
import axios from "axios";
import { API } from "../../../utils/Api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const PettyCashTransactions = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const storedOutletData = localStorage.getItem("outletData");
    const outletData = JSON.parse(storedOutletData || "{}");

    const [transactions, setTransactions] = useState([]);
    const [loadingTxns, setLoadingTxns] = useState(false);

    // Pagination (client-side on filtered array)
    const itemsPerPage = 10;
    const [currentPage, setCurrentPage] = useState(1);

    // Filters (UI)
    const [typeFilter, setTypeFilter] = useState("ALL"); // ALL | CREDIT | DEBIT
    const [startDate, setStartDate] = useState(""); // yyyy-mm-dd
    const [endDate, setEndDate] = useState(""); // yyyy-mm-dd
    const [searchText, setSearchText] = useState("");

    // Applied filters (for “Get” button UX like ProductSales)
    const [appliedFilters, setAppliedFilters] = useState({
        typeFilter: "ALL",
        startDate: "",
        endDate: "",
    });

    // Delete state
    const [deletingId, setDeletingId] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedTxn, setSelectedTxn] = useState(null);

    // Items view dialog
    const [itemsOpen, setItemsOpen] = useState(false);
    const [itemsTxn, setItemsTxn] = useState(null);

    const formatAmount = (value) =>
        Number(value || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

    const formatDateTime = (iso) => {
        if (!iso) return "-";
        const d = new Date(iso);
        return d.toLocaleString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    };

    const isValidDateRange = (s, e) => {
        if (!s || !e) return true;
        // ISO yyyy-mm-dd works with string compare, but we’ll be safe:
        const sd = new Date(s);
        const ed = new Date(e);
        return sd <= ed;
    };

    const getPettyCashTransactions = async (filterType, sDate, eDate) => {
        if (!outletData?._id) return;

        if (!isValidDateRange(sDate, eDate)) {
            toast.error("Start Date cannot be greater than End Date", {
                autoClose: 3000,
            });
            return;
        }

        try {
            setLoadingTxns(true);
            const requestUrl = `${API.BASE_URL}outlet/petty-cash/transactions`;

            const params = { outlet: outletData?._id };

            if (filterType && filterType !== "ALL") params.type = filterType;
            if (sDate) params.fromDate = sDate;
            if (eDate) params.toDate = eDate;

            const response = await axios.get(requestUrl, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            const list = response?.data?.data?.outletPettyCashTransactions || [];
            setTransactions(Array.isArray(list) ? list : []);
            setCurrentPage(1);
        } catch (error) {
            console.error("Error fetching petty cash transactions:", error);
            toast.error("Failed to load transactions. Please try again.", {
                autoClose: 3000,
            });
            setTransactions([]);
        } finally {
            setLoadingTxns(false);
        }
    };

    const handleGet = () => {
        const next = { typeFilter, startDate, endDate };
        setAppliedFilters(next);
        getPettyCashTransactions(next.typeFilter, next.startDate, next.endDate);
    };

    const handleClearFilters = () => {
        setTypeFilter("ALL");
        setStartDate("");
        setEndDate("");
        setSearchText("");
        const next = { typeFilter: "ALL", startDate: "", endDate: "" };
        setAppliedFilters(next);
        getPettyCashTransactions(next.typeFilter, next.startDate, next.endDate);
    };

    const openDeleteDialog = (txn) => {
        setSelectedTxn(txn);
        setConfirmOpen(true);
    };

    const handleDialogClose = () => {
        if (!deletingId) {
            setConfirmOpen(false);
            setSelectedTxn(null);
        }
    };

    const handleConfirmDelete = async () => {
        if (!selectedTxn?._id) return;

        const txnId = selectedTxn._id;

        try {
            setDeletingId(txnId);
            const deleteUrl = `${API.BASE_URL}outlet/petty-cash/transactions`;

            await axios.delete(deleteUrl, {
                data: { _id: txnId },
                headers: { Authorization: `Bearer ${token}` },
            });

            toast.success("Transaction deleted successfully.", { autoClose: 2500 });

            await getPettyCashTransactions(
                appliedFilters.typeFilter,
                appliedFilters.startDate,
                appliedFilters.endDate
            );
        } catch (error) {
            console.error("Error deleting petty cash transaction:", error);
            toast.error("Failed to delete transaction. Please try again.", {
                autoClose: 3000,
            });
        } finally {
            setDeletingId(null);
            setConfirmOpen(false);
            setSelectedTxn(null);
        }
    };

    const openItemsDialog = (txn) => {
        setItemsTxn(txn);
        setItemsOpen(true);
    };

    const closeItemsDialog = () => {
        setItemsOpen(false);
        setItemsTxn(null);
    };

    // Initial fetch (similar to ProductSales mount fetch)
    useEffect(() => {
        if (!outletData?._id) return;
        getPettyCashTransactions(appliedFilters.typeFilter, appliedFilters.startDate, appliedFilters.endDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outletData?._id]);

    // Local search filtering (remark / type / amount / date)
    const filteredTransactions = useMemo(() => {
        const rows = Array.isArray(transactions) ? transactions : [];
        const q = searchText.trim().toLowerCase();
        if (!q) return rows;

        return rows.filter((t) => {
            const remark = (t?.remark || "").toLowerCase();
            const type = (t?.type || "").toLowerCase();
            const amt = String(t?.amount ?? "").toLowerCase();
            const dt = formatDateTime(t?.createdAt).toLowerCase();
            const inv = (t?.invoice || "").toLowerCase();
            return (
                remark.includes(q) ||
                type.includes(q) ||
                amt.includes(q) ||
                dt.includes(q) ||
                inv.includes(q)
            );
        });
    }, [transactions, searchText]);

    // Summary chips
    const summary = useMemo(() => {
        const rows = Array.isArray(filteredTransactions) ? filteredTransactions : [];
        return rows.reduce(
            (acc, t) => {
                const type = String(t?.type || "").toUpperCase();
                const amt = Number(t?.amount || 0);
                if (type === "CREDIT") acc.totalCredit += amt;
                if (type === "DEBIT") acc.totalDebit += amt;
                acc.net = acc.totalCredit - acc.totalDebit;
                return acc;
            },
            { totalCredit: 0, totalDebit: 0, net: 0 }
        );
    }, [filteredTransactions]);

    // Pagination (client-side)
    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage) || 1;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const paginatedTransactions = filteredTransactions.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (event, page) => setCurrentPage(page);

    const renderItemsTable = (items) => {
        if (!items || items.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    No item breakdown available.
                </Typography>
            );
        }

        return (
            <Box sx={{ mt: 1 }}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr 1fr",
                        fontSize: 12,
                        fontWeight: 800,
                        color: "#334155",
                        mb: 0.8,
                    }}
                >
                    <Box>Item</Box>
                    <Box textAlign="right">Qty</Box>
                    <Box textAlign="right">Amount</Box>
                </Box>
                <Divider sx={{ mb: 1 }} />
                {items.map((it, idx) => (
                    <Box
                        key={it?._id || idx}
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "2fr 1fr 1fr",
                            fontSize: 13,
                            mb: 0.5,
                            color: "#0f172a",
                        }}
                    >
                        <Box sx={{ pr: 1 }}>{it?.itemName || "Item"}</Box>
                        <Box textAlign="right">{Number(it?.quantity || 0)}</Box>
                        <Box textAlign="right">₹{formatAmount(it?.amount ?? (Number(it?.price || 0) * Number(it?.quantity || 0)))}</Box>
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <>
            <Box className="dashBoard" sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
                <Toolbar />

                <Grid container justifyContent="center" sx={{ pb: 6 }}>
                    <Grid item xs={12} md={11}>
                        {/* Header */}
                        <Card
                            elevation={0}
                            sx={{
                                mt: 4,
                                borderRadius: 3,
                                border: "1px solid #e6eaf0",
                                background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                            }}
                        >
                            <CardContent sx={{ py: 3 }}>
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    spacing={2}
                                >
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <KeyboardArrowLeftOutlined
                                            sx={{ cursor: "pointer" }}
                                            onClick={() => navigate(-1)}
                                            fontSize="large"
                                        />

                                        <Box
                                            sx={(theme) => ({
                                                width: 46,
                                                height: 46,
                                                borderRadius: 2,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                backgroundColor: theme.palette.primary.light + "22",
                                                color: theme.palette.primary.main,
                                            })}
                                        >
                                            <CurrencyRupeeSharp fontSize="medium" />
                                        </Box>

                                        <Box>
                                            <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                                                Petty Cash History
                                            </Typography>
                                            <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                                                Filter by type and date range, then view transactions (debits can be deleted).
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                        <Chip
                                            icon={<FilterAltOutlined />}
                                            label={`Records: ${filteredTransactions?.length || 0}`}
                                            sx={{ fontWeight: 800, borderRadius: 2 }}
                                            variant="outlined"
                                        />
                                        {transactions?.length > 0 && (
                                            <Chip
                                                label={`Last updated: ${formatDateTime(transactions[0]?.createdAt)}`}
                                                sx={{ fontWeight: 700, borderRadius: 2 }}
                                                variant="outlined"
                                            />
                                        )}
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>

                        {/* Filters */}
                        <Card
                            elevation={0}
                            sx={{
                                mt: 2,
                                borderRadius: 3,
                                border: "1px solid #e6eaf0",
                                background: "#fff",
                            }}
                        >
                            <CardContent sx={{ py: 2 }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={3}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155", mb: 0.8 }}>
                                            Type
                                        </Typography>

                                        <ToggleButtonGroup
                                            fullWidth
                                            size="small"
                                            value={typeFilter}
                                            exclusive
                                            onChange={(e, val) => val && setTypeFilter(val)}
                                            sx={{
                                                "& .MuiToggleButton-root": {
                                                    borderRadius: 2,
                                                    height: 52,
                                                    fontWeight: 900,
                                                    textTransform: "none",
                                                },
                                            }}
                                        >
                                            <ToggleButton value="ALL">All</ToggleButton>
                                            <ToggleButton value="CREDIT">Credit</ToggleButton>
                                            <ToggleButton value="DEBIT">Debit</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Grid>

                                    <Grid item xs={12} md={2}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155", mb: 0.8 }}>
                                            Start Date
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 2,
                                                    height: 52,
                                                    background: "#fff",
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={2}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155", mb: 0.8 }}>
                                            End Date
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 2,
                                                    height: 52,
                                                    background: "#fff",
                                                },
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155", mb: 0.8 }}>
                                            Search
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            placeholder="Search by remark / amount / date"
                                            value={searchText}
                                            onChange={(e) => {
                                                setSearchText(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: 2,
                                                    height: 52,
                                                    background: "#fff",
                                                },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <Box sx={{ display: "flex", alignItems: "center", pr: 1, color: "#64748b" }}>
                                                        <SearchRounded />
                                                    </Box>
                                                ),
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={2}>
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 800,
                                                color: "#334155",
                                                mb: 0.8,
                                                textAlign: "center",
                                            }}
                                        >
                                            Actions
                                        </Typography>

                                        <Stack direction="row" spacing={1} justifyContent="space-between">
                                            <Button
                                                variant="contained"
                                                disabled={loadingTxns}
                                                onClick={handleGet}
                                                sx={{
                                                    height: 52,
                                                    width: "70%",
                                                    borderRadius: 2,
                                                    fontWeight: 900,
                                                    textTransform: "none",
                                                    backgroundColor: "#003049",
                                                    "&:hover": { backgroundColor: "#00263a" },
                                                }}
                                            >
                                                {loadingTxns ? "Loading..." : "Get"}
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                onClick={handleClearFilters}
                                                disabled={loadingTxns}
                                                sx={{
                                                    height: 52,
                                                    width: "30%",
                                                    borderRadius: 2,
                                                    fontWeight: 900,
                                                    textTransform: "none",
                                                }}
                                            >
                                                Clear
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Content */}
                        <Card
                            elevation={0}
                            sx={{
                                mt: 2,
                                borderRadius: 3,
                                border: "1px solid #e6eaf0",
                                overflow: "hidden",
                                background: "#fff",
                            }}
                        >
                            <CardContent sx={{ p: 0 }}>
                                {loadingTxns ? (
                                    <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                                        <CircularProgress />
                                        <Typography sx={{ color: "#334155", fontWeight: 800 }}>Fetching Transactions...</Typography>
                                    </Box>
                                ) : filteredTransactions?.length === 0 ? (
                                    <Box sx={{ py: 6, textAlign: "center" }}>
                                        <Typography sx={{ fontSize: 16, fontWeight: 900, color: "#0f172a" }}>
                                            No petty cash transactions to display
                                        </Typography>
                                        <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                                            Select filters and click “Get” to view results.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        {/* Summary chips */}
                                        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#fff" }}>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                useFlexGap
                                                flexWrap="wrap"
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                    <Chip
                                                        icon={<FilterAltOutlined />}
                                                        label={`Rows: ${filteredTransactions?.length || 0}`}
                                                        variant="outlined"
                                                        sx={{ fontWeight: 900, borderRadius: 2 }}
                                                    />
                                                    <Chip
                                                        label={`Credit: ₹${formatAmount(summary.totalCredit)}`}
                                                        variant="outlined"
                                                        sx={{ fontWeight: 900, borderRadius: 2 }}
                                                    />
                                                    <Chip
                                                        label={`Debit: ₹${formatAmount(summary.totalDebit)}`}
                                                        variant="outlined"
                                                        sx={{ fontWeight: 900, borderRadius: 2 }}
                                                    />
                                                </Stack>

                                                <Chip
                                                    label={`Net: ₹${formatAmount(summary.net)}`}
                                                    sx={{
                                                        fontWeight: 900,
                                                        borderRadius: 2,
                                                        backgroundColor: "#003049",
                                                        color: "#fff",
                                                        "& .MuiChip-label": { color: "#fff" },
                                                    }}
                                                />
                                            </Stack>
                                        </Box>

                                        <TableContainer
                                            component={Paper}
                                            elevation={0}
                                            sx={{
                                                borderRadius: 0,
                                                maxHeight: "70vh",
                                                overflow: "auto",
                                            }}
                                        >
                                            <Table stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        {["No.", "Type", "Remark", "Date", "Amount", "Items", "Invoice", "Actions"].map((h) => (
                                                            <TableCell
                                                                key={h}
                                                                sx={{
                                                                    fontWeight: 900,
                                                                    color: "#0f172a",
                                                                    background: "#f1f5f9",
                                                                    borderBottom: "1px solid #e2e8f0",
                                                                    whiteSpace: "nowrap",
                                                                    position: "sticky",
                                                                    top: 0,
                                                                    zIndex: 2,
                                                                }}
                                                                align={["Remark"].includes(h) ? "left" : "center"}
                                                            >
                                                                {h}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {paginatedTransactions.map((txn, index) => {
                                                        const srNo = indexOfFirstItem + index + 1;
                                                        const typeUpper = String(txn?.type || "-").toUpperCase();
                                                        const isCredit = typeUpper === "CREDIT";
                                                        const isDebit = typeUpper === "DEBIT";

                                                        return (
                                                            <TableRow
                                                                key={txn?._id || srNo}
                                                                hover
                                                                sx={{
                                                                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                                                    "& td": { borderBottom: "1px solid #f1f5f9" },
                                                                }}
                                                            >
                                                                <TableCell align="center" sx={{ fontWeight: 800, color: "#334155" }}>
                                                                    {srNo}
                                                                </TableCell>

                                                                <TableCell align="center">
                                                                    <Chip
                                                                        size="small"
                                                                        label={typeUpper}
                                                                        sx={{
                                                                            fontWeight: 900,
                                                                            borderRadius: 2,
                                                                            backgroundColor: isCredit
                                                                                ? "rgba(46, 125, 50, 0.12)"
                                                                                : "rgba(198, 40, 40, 0.12)",
                                                                            color: isCredit ? "#2e7d32" : "#c62828",
                                                                        }}
                                                                    />
                                                                </TableCell>

                                                                <TableCell align="left">
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize: 14,
                                                                            fontWeight: 900,
                                                                            color: "#0f172a",
                                                                            textTransform: "capitalize",
                                                                        }}
                                                                    >
                                                                        {txn?.remark || (isCredit ? "Credit entry" : "Debit entry")}
                                                                    </Typography>

                                                                    {txn?.items?.length > 0 && (
                                                                        <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.3 }}>
                                                                            {txn.items.length} item{txn.items.length > 1 ? "s" : ""}
                                                                        </Typography>
                                                                    )}
                                                                </TableCell>

                                                                <TableCell align="center" sx={{ fontWeight: 700, color: "#334155" }}>
                                                                    {formatDateTime(txn?.createdAt)}
                                                                </TableCell>

                                                                <TableCell
                                                                    align="center"
                                                                    sx={{
                                                                        fontWeight: 900,
                                                                        color: isCredit ? "#2e7d32" : "#c62828",
                                                                    }}
                                                                >
                                                                    ₹{formatAmount(txn?.amount)}
                                                                </TableCell>

                                                                <TableCell align="center">
                                                                    {isDebit && txn?.items?.length > 0 ? (
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            onClick={() => openItemsDialog(txn)}
                                                                            startIcon={<ExpandMoreIcon />}
                                                                            sx={{
                                                                                borderRadius: 2,
                                                                                textTransform: "none",
                                                                                fontWeight: 900,
                                                                            }}
                                                                        >
                                                                            View
                                                                        </Button>
                                                                    ) : (
                                                                        <Typography sx={{ fontSize: 12, color: "#64748b" }}>—</Typography>
                                                                    )}
                                                                </TableCell>

                                                                <TableCell align="center">
                                                                    {txn?.invoice ? (
                                                                        <Button
                                                                            size="small"
                                                                            variant="text"
                                                                            onClick={() => window.open(txn.invoice, "_blank")}
                                                                            sx={{ textTransform: "none", fontWeight: 900 }}
                                                                            startIcon={<ReceiptLongOutlined />}
                                                                        >
                                                                            Open
                                                                        </Button>
                                                                    ) : (
                                                                        <Typography sx={{ fontSize: 12, color: "#64748b" }}>—</Typography>
                                                                    )}
                                                                </TableCell>

                                                                <TableCell align="center">
                                                                    {isDebit ? (
                                                                        <Button
                                                                            size="small"
                                                                            variant="contained"
                                                                            color="error"
                                                                            startIcon={<DeleteOutlineIcon />}
                                                                            disabled={deletingId === txn?._id}
                                                                            onClick={() => openDeleteDialog(txn)}
                                                                            sx={{
                                                                                borderRadius: 2,
                                                                                textTransform: "none",
                                                                                fontWeight: 900,
                                                                            }}
                                                                        >
                                                                            {deletingId === txn?._id ? "Deleting..." : "Delete"}
                                                                        </Button>
                                                                    ) : (
                                                                        <Typography sx={{ fontSize: 12, color: "#64748b" }}>—</Typography>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <Divider />

                                        <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                                            <Pagination
                                                count={totalPages}
                                                page={currentPage}
                                                onChange={handlePageChange}
                                                color="primary"
                                                variant="outlined"
                                                shape="rounded"
                                            />
                                        </Box>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Delete confirm dialog */}
            <Dialog open={confirmOpen} onClose={handleDialogClose} maxWidth="xs" fullWidth>
                <DialogTitle>Delete transaction?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this debit transaction? This action cannot be undone.
                    </DialogContentText>

                    {selectedTxn && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight={800}>
                                {selectedTxn?.remark || "Debit entry"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Amount: ₹ {formatAmount(selectedTxn?.amount)}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} disabled={!!deletingId} sx={{ fontWeight: 900 }}>
                        Cancel
                    </Button>
                    <Button color="error" onClick={handleConfirmDelete} disabled={!!deletingId} sx={{ fontWeight: 900 }}>
                        {deletingId ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Items dialog */}
      // ✅ Replace ONLY your Items Dialog block with this improved one

            <Dialog
                open={itemsOpen}
                onClose={closeItemsDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid #e6eaf0",
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                        borderBottom: "1px solid #e6eaf0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#00304914",
                                color: "#003049",
                            }}
                        >
                            <ReceiptLongOutlined />
                        </Box>

                        <Box>
                            <Typography sx={{ fontWeight: 900, color: "#0f172a", fontSize: 18 }}>
                                Debit Items
                            </Typography>
                            <Typography sx={{ color: "#64748b", fontSize: 12, mt: 0.3 }}>
                                Item-wise breakup for this debit transaction
                            </Typography>
                        </Box>
                    </Stack>

                    <IconButton onClick={closeItemsDialog} sx={{ color: "#0f172a" }}>
                        ✕
                    </IconButton>
                </Box>

                <DialogContent sx={{ p: 2.5, background: "#fff" }}>
                    {itemsTxn ? (
                        <>
                            <Card
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    border: "1px solid #e6eaf0",
                                    overflow: "hidden",
                                }}
                            >
                                <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#f8fafc" }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 900, color: "#0f172a" }}>
                                        Items
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.2 }}>
                                        Quantity and amount per item
                                    </Typography>
                                </Box>

                                <Box sx={{ p: 2 }}>
                                    {itemsTxn?.items?.length ? (
                                        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: "1px solid #eef2f7" }}>
                                            <Table stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        {["#", "Item", "Qty", "Amount"].map((h) => (
                                                            <TableCell
                                                                key={h}
                                                                sx={{
                                                                    fontWeight: 900,
                                                                    color: "#0f172a",
                                                                    background: "#f1f5f9",
                                                                    borderBottom: "1px solid #e2e8f0",
                                                                    whiteSpace: "nowrap",
                                                                }}
                                                                align={h === "Item" ? "left" : "center"}
                                                            >
                                                                {h}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {itemsTxn.items.map((it, idx) => (
                                                        <TableRow
                                                            key={it?._id || idx}
                                                            sx={{
                                                                backgroundColor: idx % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                                                "& td": { borderBottom: "1px solid #f1f5f9" },
                                                            }}
                                                        >
                                                            <TableCell align="center" sx={{ fontWeight: 800, color: "#334155" }}>
                                                                {idx + 1}
                                                            </TableCell>

                                                            <TableCell align="left">
                                                                <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#0f172a" }}>
                                                                    {it?.itemName || "Item"}
                                                                </Typography>
                                                            </TableCell>

                                                            <TableCell align="center" sx={{ fontWeight: 800 }}>
                                                                {Number(it?.quantity || 0)}
                                                            </TableCell>

                                                            <TableCell align="center" sx={{ fontWeight: 900, color: "#0f172a" }}>
                                                                ₹{formatAmount(it?.amount ?? (Number(it?.price || 0) * Number(it?.quantity || 0)))}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No item breakdown available.
                                        </Typography>
                                    )}

                                    {/* Optional footer note */}
                                    <Typography sx={{ mt: 1.2, fontSize: 11, color: "#64748b" }}>
                                        Note: Amounts are shown item-wise as stored in the transaction.
                                    </Typography>
                                </Box>
                            </Card>
                        </>
                    ) : null}
                </DialogContent>

                {/* Footer */}
                <DialogActions
                    sx={{
                        px: 2.5,
                        py: 1.5,
                        borderTop: "1px solid #e6eaf0",
                        background: "#fff",
                    }}
                >
                    <Button
                        onClick={closeItemsDialog}
                        sx={{
                            fontWeight: 900,
                            textTransform: "none",
                            borderRadius: 2,
                        }}
                        variant="outlined"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Toastify container – remove if you already have one at app root */}
            <ToastContainer
                position="bottom-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />
        </>
    );
};

export default PettyCashTransactions;